import React, { useState, useEffect, useCallback } from 'react';
import { Profile } from '../../hooks/useAuth';
import {
  Clock,
  CheckCircle2,
  AlertCircle,
  Building2,
  Search,
  Filter,
  Check,
  X,
  CreditCard,
} from 'lucide-react';
import { format } from 'date-fns';
import { apiFetch } from '../../apiClient';
import { Card } from '../ui/Card';

interface PayoutEntry {
  id: string;
  provider_id: string;
  amount: number;
  status: 'requested' | 'processing' | 'paid' | 'rejected';
  transaction_ref: string | null;
  bank_details: Record<string, string>;
  created_at: string;
  processed_at: string | null;
  business_name: string;
  slug: string;
}

const PAYOUT_STATUS_STYLES: Record<string, string> = {
  requested:  'bg-line text-ink-muted',
  processing: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  paid:       'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  rejected:   'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
};

export const PayoutManagement: React.FC<{ profile: Profile }> = ({ profile }) => {
  const [payouts, setPayouts] = useState<PayoutEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [txnRef, setTxnRef] = useState('');

  const fetchPayouts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiFetch('/admin/payouts');
      if (!data.success) throw new Error(data.message);
      setPayouts(data.payouts || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPayouts();
  }, [fetchPayouts]);

  const handleUpdateStatus = async (id: string, status: string, ref?: string) => {
    try {
      setProcessingId(id);
      const data = await apiFetch(`/admin/payouts/${id}/status`, {
        method: 'POST',
        body: JSON.stringify({ status, transaction_ref: ref || txnRef }),
      });
      if (!data.success) throw new Error(data.message);
      setTxnRef('');
      setProcessingId(null);
      fetchPayouts();
    } catch (err: any) {
      alert(err.message);
      setProcessingId(null);
    }
  };

  const filteredPayouts = payouts.filter(p => {
    const matchesSearch = p.business_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         p.transaction_ref?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    pending: payouts.filter(p => p.status === 'requested').reduce((acc, p) => acc + Number(p.amount), 0),
    processing: payouts.filter(p => p.status === 'processing').reduce((acc, p) => acc + Number(p.amount), 0),
    totalPaid: payouts.filter(p => p.status === 'paid').reduce((acc, p) => acc + Number(p.amount), 0),
  };

  if (loading) return (
    <div className="p-12 flex items-center justify-center">
      <p className="label-caps text-ink-faint">Loading payouts...</p>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto p-6 md:p-10 space-y-8">
      <div>
        <h2 className="display text-2xl md:text-3xl text-ink">Payouts</h2>
        <p className="label-caps text-ink-faint mt-2">Provider withdrawal requests</p>
      </div>

      {error && (
        <div className="p-4 flex items-center gap-3 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 rounded-sm border border-red-200 dark:border-red-900 text-xs font-semibold">
          <AlertCircle size={16} className="shrink-0" /> {error}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        <div className="bg-inverse text-ink-inverse p-8 rounded-sm border border-line">
          <p className="label-caps text-slate-400 mb-4">Awaiting payout</p>
          <h3 className="display text-4xl leading-none mb-6">RM {stats.pending.toFixed(2)}</h3>
          <p className="label-caps text-accent flex items-center gap-2">
            <Clock size={14} /> Requested by providers
          </p>
        </div>

        <Card className="p-8">
          <p className="label-caps text-ink-faint mb-4">Processing</p>
          <h3 className="display text-4xl text-ink leading-none mb-6">RM {stats.processing.toFixed(2)}</h3>
          <p className="label-caps text-amber-700 dark:text-amber-400 flex items-center gap-2">
            <CreditCard size={14} /> Settlement in progress
          </p>
        </Card>

        <Card className="p-8">
          <p className="label-caps text-ink-faint mb-4">Total paid</p>
          <h3 className="display text-4xl text-ink leading-none mb-6">RM {stats.totalPaid.toFixed(2)}</h3>
          <p className="label-caps text-green-600 dark:text-green-400 flex items-center gap-2">
            <CheckCircle2 size={14} /> Lifetime settled
          </p>
        </Card>
      </div>

      {/* Controls */}
      <Card className="p-4 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint" size={16} />
          <input
            type="text"
            placeholder="Search by provider or reference..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 min-h-[44px] bg-surface border border-line rounded-sm text-xs font-semibold text-ink placeholder:text-ink-faint outline-none focus:border-accent transition-colors"
          />
        </div>

        <div className="flex items-center gap-2">
          <Filter size={16} className="text-ink-faint" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-surface border border-line rounded-sm px-4 min-h-[44px] text-xs font-semibold text-ink outline-none focus:border-accent transition-colors"
          >
            <option value="all">All statuses</option>
            <option value="requested">Requested</option>
            <option value="processing">Processing</option>
            <option value="paid">Paid</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </Card>

      {/* Payout table */}
      <Card className="overflow-x-auto">
        <table className="w-full text-left border-collapse min-w-[860px]">
          <thead className="bg-inverse">
            <tr>
              <th className="px-6 py-4 label-caps text-slate-400">Provider</th>
              <th className="px-6 py-4 label-caps text-slate-400">Bank details</th>
              <th className="px-6 py-4 label-caps text-slate-400">Amount</th>
              <th className="px-6 py-4 label-caps text-slate-400 text-center">Status</th>
              <th className="px-6 py-4 label-caps text-slate-400 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {filteredPayouts.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-20 text-center">
                  <p className="display text-base text-ink-muted">No matching payout requests</p>
                </td>
              </tr>
            ) : (
              filteredPayouts.map(payout => (
                <tr key={payout.id} className="hover:bg-surface transition-colors">
                  <td className="px-6 py-6">
                    <p className="text-sm font-semibold text-ink">{payout.business_name}</p>
                    <p className="text-xs text-ink-faint mt-1">
                      {format(new Date(payout.created_at), 'dd MMM yyyy, HH:mm')}
                    </p>
                  </td>
                  <td className="px-6 py-6">
                    <div className="space-y-1">
                      <p className="text-xs font-semibold text-ink flex items-center gap-2">
                        <Building2 size={12} className="text-accent" /> {payout.bank_details?.bank_name || '—'}
                      </p>
                      <p className="text-xs font-mono text-ink-muted">
                        {payout.bank_details?.account_number || '—'}
                      </p>
                      <p className="text-xs text-ink-faint truncate max-w-[150px]">
                        {payout.bank_details?.account_name || '—'}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-6">
                    <p className="display text-xl text-ink">RM {Number(payout.amount).toFixed(2)}</p>
                  </td>
                  <td className="px-6 py-6">
                    <div className="flex justify-center">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-sm label-caps ${PAYOUT_STATUS_STYLES[payout.status] ?? 'bg-line text-ink-muted'}`}>
                        {payout.status}
                      </span>
                    </div>
                    {payout.transaction_ref && (
                      <p className="text-[10px] text-ink-faint text-center mt-2 font-mono uppercase">
                        {payout.transaction_ref}
                      </p>
                    )}
                  </td>
                  <td className="px-6 py-6 text-right">
                    <div className="flex flex-col gap-2 items-end">
                      {payout.status === 'requested' && (
                        <button
                          onClick={() => handleUpdateStatus(payout.id, 'processing')}
                          className="px-4 min-h-[36px] bg-inverse text-ink-inverse dark:bg-accent dark:text-accent-contrast label-caps rounded-sm hover:opacity-90 transition-opacity"
                        >
                          Mark processing
                        </button>
                      )}

                      {(payout.status === 'requested' || payout.status === 'processing') && (
                        <div className="flex gap-2">
                          <input
                            type="text"
                            placeholder="Txn ref..."
                            className="px-3 min-h-[36px] bg-surface border border-line rounded-sm text-xs font-semibold text-ink uppercase w-32 outline-none focus:border-accent transition-colors"
                            onChange={(e) => setTxnRef(e.target.value)}
                            disabled={processingId === payout.id}
                          />
                          <button
                            onClick={() => handleUpdateStatus(payout.id, 'paid')}
                            disabled={(!txnRef && !payout.transaction_ref) || processingId === payout.id}
                            className="p-2 bg-green-600 text-white rounded-sm hover:bg-green-700 transition-colors disabled:opacity-30"
                            title="Mark as paid"
                          >
                            <Check size={16} />
                          </button>
                          <button
                            onClick={() => {
                              const reason = prompt('Reason for rejection?');
                              if (reason) handleUpdateStatus(payout.id, 'rejected', `REJECTED: ${reason}`);
                            }}
                            className="p-2 bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300 rounded-sm hover:opacity-80 transition-opacity"
                            title="Reject request"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      )}

                      {payout.status === 'paid' && (
                        <span className="flex items-center gap-2 text-green-600 dark:text-green-400 label-caps">
                          <CheckCircle2 size={14} /> Paid
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
};
